<template>
    <div>
        <el-upload
            class="video-upload"
            drag
            :action="uploadApi.video"
            accept="audio/mpeg, audio/mp3, audio/mp4, video/mp4, video/x-m4v, video/*"
            :limit="10"
            :auto-upload="true"
            :data="uploadData"
            :before-upload="beforeUpload"
            :on-success="onSuccess"
            :on-progress="onProgress"
            :on-error="onError"
            :file-list="fileListInt"
            :on-remove="onRemove"
            :disabled="disabled || isDisabled"
            :on-exceed="onExceed"
            :on-preview="onPreview"
            v-bind="$attrs"
        >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text" v-html="desc"></div>
            <div class="el-upload__tip" slot="tip">{{ tip }}</div>
        </el-upload>
        <video-preview :video="previewVideo" ref="videoPreview"></video-preview>
    </div>
</template>

<script>
import VideoPreview from './VideoPreview';
import { apiMap } from '../server';

export default {
    name: 'VideoUpload',
    components: {
        VideoPreview
    },
    props: {
        value: {
            type: [Object, Array]
        },
        /**
         * 视频验证规则
         */
        validateRule: {
            type: Object,
            default: function() {
                return {
                    maxSize: 200, // 单位M
                    format: ['mp4', 'x-m4v', 'quicktime', 'mp3'] // 格式
                };
            }
        },
        single: {
            type: Boolean,
            default: function() {
                return true;
            }
        },
        /**
         * 提示信息
         */
        tip: {
            type: String,
            default: function() {
                return `支持的文件格式：mp4，mov。单个视频文件大小不超过${this.validateRule.maxSize || 200}M。`;
            }
        },
        /**
         * 是否可以操作
         */
        disabled: {
            type: Boolean,
            default: function() {
                return false;
            }
        },
        /**
         * 上传时附带的额外参数
         */
        extData: {
            type: Object,
            default: function() {
                return {};
            }
        },
        /**
         * 上传文案提示
         */
        desc: {
            type: String,
            default: function() {
                return `将文件拖到此处，或
                <em>点击上传</em>`;
            }
        },
        /**
         * 上传视频分辨率限制
         * { width: 1280, height: 720 }
         */
        pixelSizeLimit: {
            type: Object,
            default: null,
        }
    },
    data() {
        return {
            uploadApi: apiMap,
            uploadData: {},
            fileList: [],
            lastValue: null,
            isDisabled: false,
            previewVideo: {
                name: null,
                url: null
            }
        };
    },
    computed: {
        fileListInt: {
            get: function() {
                if (this.lastValue !== this.value) {
                    if (!(this.value && this.value.url)) return [];
                    if (Array.isArray(this.value)) {
                        this.lastValue = this.value;
                        return this.value;
                    }
                    this.lastValue = this.value;
                    return [this.value];
                }
                return this.fileList;
            },
            set: function(v) {
                this.fileList = v;
            }
        }
    },
    watch: {
        fileList: {
            deep: true,
            handler: function() {
                let videoData = null;
                if (Array.isArray(this.value)) {
                    videoData = this.fileList;
                } else {
                    if (this.fileList && this.fileList.length) {
                        videoData = this.fileList[0];
                    }
                }
                this.$emit('input', videoData);
            }
        }
    },
    methods: {
        beforeUpload(file) {
            if (this.validateRule) {
                let isformat = true;
                if (this.validateRule.format) {
                    const formatArr = this.validateRule.format;
                    let formatStr = formatArr.join('|');
                    formatStr = `^video/(${formatStr})`;
                    if (formatArr.includes('mp3')) {
                        formatStr = `(${formatStr}|^audio/(mpeg|mp3))`;
                    }
                    let flieTypeReg = new RegExp(formatStr);
                    if (!flieTypeReg.test(file.type)) {
                        isformat = false;
                        this.$message.error('上传文件格式不正确');
                    }
                }

                const isLt = file.size / 1024 / 1024 < this.validateRule.maxSize;
                if (!isLt) {
                    this.$message.error(`上传视频大小不能超过 ${this.validateRule.maxSize || 200}MB`);
                }
                if (!(isformat && isLt)) {
                    return false;
                }
            }

            if (this.pixelSizeLimit) {
                const checkResolution = file => new Promise((resolve, reject) => {
                    let video = document.createElement('video');
                    video.preload = 'metadata';

                    video.onloadedmetadata = () => {
                        const width = video.videoWidth;
                        const height = video.videoHeight;

                        const { width: minWidth, height: minHeight } = this.pixelSizeLimit;

                        if (width >= minWidth && height >= minHeight) {
                            resolve(true);
                        } else {
                            reject(new Error(`视频分辨率至少为${minHeight}p(${minWidth}x${minHeight}像素) `));
                        }
                        window.URL.revokeObjectURL(video.src);
                        video = null;
                    };

                    video.onerror = function () {
                        reject(new Error('无法获取视频分辨率'));
                        window.URL.revokeObjectURL(video.src);
                        video = null;
                    };

                    video.src = URL.createObjectURL(file);
                });
                return checkResolution(file).then(() => {
                    this.uploadData = Object.assign(this.uploadData, this.extData);
                    this.uploadData.fileName = file.name;
                    return true;
                }).catch(error => {
                    this.$message.error(error.message);
                    return Promise.reject(error);
                });
            }

            this.uploadData = Object.assign(this.uploadData, this.extData);
            this.uploadData.fileName = file.name;
        },
        onSuccess(res, file, fileList) {
            if (file && file.response && file.response.data) {
                let newFlie = {
                    name: file.name,
                    url: file.response && file.response.data && file.response.data.url,
                    MD5: file.response && file.response.data && file.response.data.MD5,
                    mimeType: file.raw && file.raw.type
                };
                this.fileListInt = this.single ? [newFlie] : this.fileList.concat(newFlie);
            } else {
                if (!this.fileListInt.length) {
                    this.fileListInt = [{}];
                } else {
                    this.fileListInt = [].concat(this.fileListInt);
                }
                this.$message.error('上传失败，请重试 ');
            }
            this.isDisabled = false;
        },
        onError(err, file, fileList) {
            this.isDisabled = false;
            this.$message.error('上传失败，请重试 ');
        },
        onRemove(file, fileList) {
            this.fileListInt = fileList;
        },
        onProgress(event, file, fileList) {},
        onExceed(files, fileList) {
            this.$message.error('超出上传数量，请先删除已存在文件后再试');
        },
        onPreview(file) {
            this.previewVideo = file;
            this.$refs.videoPreview.show();
        }
    }
};
</script>

<style lang="scss" scoped>
.video-upload /deep/ {
    .el-upload {
        width: 100%;
        .el-upload-dragger {
            margin: 0 auto;
        }
    }
    .el-upload__tip {
        text-align: center;
    }
    .el-upload-list li {
        border: solid 1px #e1e1e1;
    }
}
</style>
